import { LIST_MAX } from '#/services/settings/limits.js'

// The WRITE path behind the drill-down's "trust this person" control (plan:
// .claude/plans/trust-from-drilldown.md). Deliberately transport-free and injectable, so the rule that
// matters can be tested without a network: a trusted-list patch REPLACES the whole list, so the new list
// must be built from a FRESH read taken immediately before the write — never from whatever the SPA has
// held in memory since the sheet was opened (which can be minutes old, and stale the moment someone runs
// /trust from Telegram). Getting that wrong silently deletes trusted users, and the only trace would be a
// settings.update audit row nobody is looking at.
//
// It does NOT catch: a read/write failure propagates to the caller, which owns the loud in-sheet error.
// A "list full" is NOT an exception — it is a legitimate, expected outcome with its own message, so it
// travels as a value (a thrown 400 `invalid_settings` would be indistinguishable from any other refusal).

export interface TrustIo {
  /** The AUTHORITATIVE trusted ids, re-read from the server. Also the caller's chance to refresh its own state. */
  read: () => Promise<readonly string[]>
  /** Persist the complete new list (PATCH /api/settings — the single write path, routes/settings.ts:38). */
  write: (userIds: string[]) => Promise<void>
}

/**
 * 'ok' = written · 'noop' = the fresh list already says what was asked (someone got there first — nothing
 * to write, but the caller must still repaint from the refreshed state) · 'full' = the list is at LIST_MAX
 * and this was an ADD (nothing written).
 */
export type TrustResult = 'ok' | 'noop' | 'full'

/** Add (`next` true) or remove (`next` false) one id from the trusted list: re-read → decide → write. */
export async function applyTrust(io: TrustIo, targetId: string, next: boolean): Promise<TrustResult> {
  const ids = await io.read()
  const has = ids.includes(targetId)
  if (has === next)
    return 'noop'
  if (next && ids.length >= LIST_MAX)
    return 'full'
  // filter() (not a single splice) removes EVERY occurrence: an id could have been duplicated by an older
  // write path, and a remove that leaves one behind would report success while the exemption still holds.
  const userIds = next ? [...ids, targetId] : ids.filter(id => id !== targetId)
  await io.write(userIds)
  return 'ok'
}
